import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import StorageService from "../../services/StorageService";
import StockInfoService from "../../services/stockInfoService";
import InventoryReportService from "../../services/inventoryReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../assets/images/deviops_logo.png';
import { addPdfLogo, getPdfLogoDataUrl } from "../../utils/pdfLogo";
import ProjectCategoryService from "../../services/ProjectCategoryService";
import ItemCategoryService from "../../services/ItemCategoryService";
import ReactPaginate from "react-paginate";

export default function ViewInventoryReport() {

  // DROPDOWNS
  const [projects, setProjects] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);

  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [itemCategories, setItemCategories] = useState([]);
  const [selectedItemCategoryId, setSelectedItemCategoryId] = useState("");
  // SELECTED VALUES
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedItemId, setSelectedItemId] = useState("");
  
  const employeeId = StorageService.loginData().employeeId;
  const roleId = StorageService.loginData().roleId;

  const comp = StorageService.activeCompany();

  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const handleSearchClick = () => {
    setCurrentPage(1);
    loadReport(1);
  };

  const handleClearFilters = () => {
    // reset dropdowns
    setSelectedCategoryId("");
    setSelectedProjectId("");
    setSelectedStoreId("");
    setSelectedItemCategoryId("");
    setSelectedItemId("");

    // reset dependent lists
    setStores([]);
    setItems([]);

    // reset pagination
    setCurrentPage(1);

    // load default data
    // loadReport();
  };

  const loadProjects = async () => {
    try {
      const res = await InventoryReportService.getProjectsForInventoryReport({
        companyId: comp.companyId,
        employeeId,
        roleId
      });

      const data = res.result || [];

      const mapped = data.map(p => ({
        id: p.id || p.projectId,
        projectName: p.projectName,
        projectCode: p.projectCode,
        projectCategoryId: p.projectCategoryId,
        projectCategory: p.projectCategory
      }));

      setProjects(mapped);
    } catch (err) {
      console.error(err);
      setProjects([]);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await ProjectCategoryService.getActive();
      const data = res?.data || res || [];

      setCategories(data);
    } catch (err) {
      console.error("Category load error", err);
      setCategories([]);
    }
  };

  const loadItemCategories = async () => {
    try {
      const res = await ItemCategoryService.getActive();
      const data = res?.data || res || [];
      setItemCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Item category load error", err);
      setItemCategories([]);
    }
  };

  const [allItems, setAllItems] = useState([]);

  const loadAllItems = async () => {
    try {
      const res = await StockInfoService.getItemsByCompany(comp?.companyId || "all", employeeId, roleId);
      const data = res?.result || res?.data || res || [];
      setAllItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Item load error", err);
      setAllItems([]);
    }
  };

  useEffect(() => {
    loadCategories();
    loadProjects();
    loadItemCategories();
    loadAllItems();
  }, []);

  const loadStores = async (projectId) => {
    if (!projectId) {
      setStores([]);
      return;
    }

    try {
      const res = await StockInfoService.getStoresByProject(projectId);
      setStores(res.result || []);
    } catch (err) {
      console.error(err);
      setStores([]);
    }
  };

  useEffect(() => {
    setSelectedStoreId("");
    setSelectedItemCategoryId("");
    setSelectedItemId("");
    setStores([]);
    setItems([]);
    setTableData([]);

    if (selectedProjectId) {
      loadStores(selectedProjectId);
    }
  }, [selectedProjectId]);

  const loadItems = async (storeId) => {
    if (!storeId) {
      setItems([]);
      return;
    }

    try {
      const res = await StockInfoService.getItemsByStore(storeId);
      setItems(res.result || []);
    } catch (err) {
      console.error(err);
      setItems([]);
    }
  };

  useEffect(() => {
    // Reset item selection when store changes
    setSelectedItemCategoryId("");
    setSelectedItemId("");
    setTableData([]);

    if (selectedStoreId) {
      loadItems(selectedStoreId);
    } else {
      setItems(allItems);
    }
  }, [selectedStoreId]);
  /* ================= VIEW REPORT ================= */
  const loadReport = async (pageNo = currentPage) => {
    try {
      setTableLoading(true);

      const res = await InventoryReportService.getReport({
        companyId: comp.companyId,
        employeeId: employeeId,
        roleId: roleId,
        projectId: selectedProjectId || null,
        storeId: selectedStoreId || null,
        itemCategoryId: selectedItemCategoryId || null,
        itemId: selectedItemId || null,
        page: pageNo,
        limit: itemsPerPage
      });

      setTableData(res.data || []);
      setPageCount(res.pagination?.totalPages || 1);
    } catch (err) {
      console.error(err);
      setTableData([]);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const handlePageClick = async (event) => {
    const page = event.selected + 1;

    setCurrentPage(page);

    await loadReport(page);
  };

  const getAllReportData = async () => {
    let allData = [];
    let pageNumber = 1;
    let totalPages = 1;

    do {
      const res = await InventoryReportService.getReport({
        companyId: comp.companyId,
        employeeId: employeeId,
        roleId: roleId,
        projectId: selectedProjectId || null,
        storeId: selectedStoreId || null,
        itemCategoryId: selectedItemCategoryId || null,
        itemId: selectedItemId || null,
        page: pageNumber,
        limit: itemsPerPage
      });

      allData = [...allData, ...(res.data || [])];
      totalPages = res.pagination?.totalPages || 1;
      pageNumber++;
    } while (pageNumber <= totalPages);

    return allData;
  };

  const exportExcel = async () => {
    const allData = await getAllReportData();

    if (!allData || !allData.length) {
      window.alert("No data to export");
      return;
    }
    const toNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const sortedData = [...allData].sort((a, b) => {
      const projA = String(a.projectName || "");
      const projB = String(b.projectName || "");
      if (projA !== projB) return projA.localeCompare(projB);

      const storeA = String(a.storeName || "");
      const storeB = String(b.storeName || "");
      if (storeA !== storeB) return storeA.localeCompare(storeB);

      const catA = String(a.itemCategory || a.itemCategoryName || "");
      const catB = String(b.itemCategory || b.itemCategoryName || "");
      if (catA !== catB) return catA.localeCompare(catB);

      const itemA = String(a.itemCode || a.itemName || "");
      const itemB = String(b.itemCode || b.itemName || "");
      return itemA.localeCompare(itemB);
    });

    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];

    const thin = { style: "thin" };
    const cellBorder = { top: thin, bottom: thin, left: thin, right: thin };
    const center = { horizontal: "center", vertical: "center", wrapText: true };
    const labelStyle = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: cellBorder
    };
    const valueStyle = {
      font: { sz: 10 },
      alignment: { horizontal: "left", vertical: "center", wrapText: true },
      border: cellBorder
    };
    const numStyle = {
      font: { sz: 10 },
      alignment: { horizontal: "right", vertical: "center" },
      border: cellBorder
    };
    const tableHeaderStyle = {
      font: { bold: true, sz: 11 },
      alignment: center,
      border: cellBorder,
      fill: { fgColor: { rgb: "FFD9D9D9" } }
    };

    const styleCells = (r, colCount, styleForCol) => {
      for (let c = 0; c < colCount; c++) {
        const cell = XLSX.utils.encode_cell({ r, c });
        if (!ws[cell]) ws[cell] = { v: "", t: "s" };
        ws[cell].s = styleForCol(c);
      }
    };

    let row = 0;
    const companyName = comp.companyName || "";
    const selectedProject = selectedProjectId
      ? projects.find((p) => String(p.id) === String(selectedProjectId))
      : null;
    const pCatInit = selectedProject ? String(selectedProject.projectCategory || selectedProject.categoryName || "").trim().charAt(0).toUpperCase() : "";
    const pFormatted = selectedProject ? (pCatInit ? `${pCatInit}*${selectedProject.projectName}` : selectedProject.projectName) : "All Projects";
    const projectLabel = selectedProject ? pFormatted : "All Projects";

    // Row 0: Company Name (Left) + Report Date (Top Right)
    const headerReportDateStr = `Report Date: ${utility.formatExportDateTime(new Date())}`;
    XLSX.utils.sheet_add_aoa(
      ws,
      [[companyName, "", "", "", headerReportDateStr, "", ""]],
      { origin: { r: row, c: 0 } }
    );
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 3 } });
    ws["!merges"].push({ s: { r: row, c: 4 }, e: { r: row, c: 6 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 12 },
      alignment: { horizontal: "left", vertical: "center" }
    };
    ws[XLSX.utils.encode_cell({ r: row, c: 4 })].s = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "right", vertical: "center" }
    };
    row++;

    // Row 1: Title (Centered across all 7 columns)
    XLSX.utils.sheet_add_aoa(ws, [["Inventory Stock Report (On-Hand)"]], { origin: { r: row, c: 0 } });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 6 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 16 },
      alignment: { horizontal: "center", vertical: "center" }
    };
    row += 2;

    // Row 3: Project Name
    XLSX.utils.sheet_add_aoa(ws, [[`Project Name: ${projectLabel}`]], { origin: { r: row, c: 0 } });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 6 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "left", vertical: "center" }
    };
    row += 2;

    // Table Headers
    const headerRow = [
      "Sr.No",
      "Store Name",
      "Item Category",
      "Item Name",
      "Item Description",
      "UOM",
      "Qty"
    ];
    XLSX.utils.sheet_add_aoa(ws, [headerRow], { origin: { r: row, c: 0 } });
    styleCells(row, 7, () => tableHeaderStyle);
    row++;

    // Data Rows
    sortedData.forEach((item, index) => {
      const qtyVal = toNumber(item.quantity);
      const rowData = [
        index + 1,
        item.storeName || "-",
        item.itemCategory || "-",
        item.itemCode || "-",
        item.itemDescription || "-",
        item.uom || "-",
        qtyVal
      ];
      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });

      // Align col 0 (Sr.No) and col 6 (Qty) to RIGHT; strings (cols 1..5) to LEFT
      styleCells(row, 7, (c) => (c === 0 || c === 6 ? numStyle : valueStyle));

      // Format numeric cells as number type 'n'
      const srCellKey = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (ws[srCellKey]) {
        ws[srCellKey].t = "n";
        ws[srCellKey].z = "0";
      }

      const qtyCellKey = XLSX.utils.encode_cell({ r: row, c: 6 });
      if (ws[qtyCellKey]) {
        ws[qtyCellKey].t = "n";
        ws[qtyCellKey].z = "#,##0.000";
      }

      row++;
    });

    // Totals Row
    const totalQuantity = sortedData.reduce((sum, item) => sum + toNumber(item.quantity), 0);
    const totalsRow = ["", "", "", "", "", "Total:", totalQuantity];
    XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: { r: row, c: 0 } });
    styleCells(row, 7, (c) => {
      if (c === 5) return { ...labelStyle, alignment: { horizontal: "right", vertical: "center" } };
      if (c === 6) return { ...numStyle, font: { bold: true } };
      return valueStyle;
    });

    const totalQtyCellKey = XLSX.utils.encode_cell({ r: row, c: 6 });
    if (ws[totalQtyCellKey]) {
      ws[totalQtyCellKey].t = "n";
      ws[totalQtyCellKey].z = "#,##0.000";
    }

    // Dynamic Column Widths
    const minWidths = [10, 25, 30, 40, 35, 15, 18];
    const colWidths = [];
    for (let c = 0; c < 7; c++) {
      let maxLen = 0;
      Object.keys(ws).forEach((cellKey) => {
        if (cellKey.startsWith("!")) return;
        const pos = XLSX.utils.decode_cell(cellKey);
        if (pos.c === c) {
          if (pos.r <= 3) return; // Skip merged header/title rows
          const cell = ws[cellKey];
          if (cell && cell.v !== undefined && cell.v !== null) {
            let valStr = String(cell.v);
            if (cell.t === "n" && typeof cell.v === "number") {
              valStr = cell.v.toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
            }
            if (valStr.length > maxLen) {
              maxLen = valStr.length;
            }
          }
        }
      });
      colWidths[c] = { wch: Math.max(maxLen + 4, minWidths[c] || 12) };
    }
    ws["!cols"] = colWidths;

    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: row, c: 6 }
    });

    const workbook = { Sheets: { InventoryStockReport: ws }, SheetNames: ["InventoryStockReport"] };
    const fileName = `InventoryStockReport_${utility.formatDateDMY(new Date()).replace(/\//g, "-")}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const exportPdf = async () => {
    const allData = await getAllReportData();

    if (!allData || !allData.length) {
      window.alert("No data to export");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const companyName = comp.companyName || "";
    const selectedProject = selectedProjectId
      ? projects.find((p) => String(p.id) === String(selectedProjectId))
      : null;
    const pdfCatInit = selectedProject ? String(selectedProject.projectCategory || selectedProject.categoryName || "").trim().charAt(0).toUpperCase() : "";
    const pdfPFormatted = selectedProject ? (pdfCatInit ? `${pdfCatInit}*${selectedProject.projectName}` : selectedProject.projectName) : "All Projects";
    const projectLabel = selectedProject ? pdfPFormatted : "All Projects";

    // Pre-convert logo to Data URL for 100% reliable rendering in production build
    let pdfLogo = null;
    try {
      pdfLogo = await getPdfLogoDataUrl(logo);
    } catch (e) {
      console.error("PDF logo load error:", e);
    }

    doc.setFont(undefined, "bold");
    doc.setFontSize(12);
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;
    const pageMargin = 14;

    const companyNameWidth = doc.getTextWidth(companyName);
    const logoWidth = 5;
    const logoHeight = 10;
    const logoX = pageMargin + 6 + (companyNameWidth - logoWidth) / 2;
    const logoY = 8;

    if (pdfLogo) {
      doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
    }

    doc.text(companyName, pageMargin + 6, 23);

    // Title Y = 35 with underline Y = 37
    doc.setFontSize(18);
    const title = "Inventory Stock Report (On-Hand)";
    doc.text(title, centerX, 35, { align: "center" });
    const titleWidth = doc.getTextWidth(title);
    doc.setLineWidth(0.3);
    doc.line(centerX - titleWidth / 2, 37, centerX + titleWidth / 2, 37);

    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("Project Name:", pageMargin + 6, 45);
    const labelWidth = doc.getTextWidth("Project Name:");
    doc.setFont(undefined, "normal");
    doc.text(` ${projectLabel}`, pageMargin + 6 + labelWidth, 45);

    // Report Date
    const reportPrefix = "Report Date: ";
    const reportDateStr = utility.formatExportDateTime(new Date());

    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    const prefixWidth = doc.getTextWidth(reportPrefix);

    doc.setFont(undefined, "normal");
    doc.setFontSize(10);
    const dateWidth = doc.getTextWidth(reportDateStr);

    const totalWidth = prefixWidth + dateWidth;
    const right = pageWidth - pageMargin; // 297 - 14 = 283
    const startX = right - totalWidth;

    doc.setFont(undefined, "bold");
    doc.text(reportPrefix, startX, 23);

    doc.setFont(undefined, "normal");
    doc.text(reportDateStr, startX + prefixWidth, 23);

    const headers = [[
      "Sr.No",
      "Store Name",
      "Item Category",
      "Item Name",
      "Item Description",
      "UOM",
      "Qty"
    ]];
    const sortedData = [...allData].sort((a, b) => {
      const projA = String(a.projectName || "");
      const projB = String(b.projectName || "");
      if (projA !== projB) return projA.localeCompare(projB);

      const storeA = String(a.storeName || "");
      const storeB = String(b.storeName || "");
      if (storeA !== storeB) return storeA.localeCompare(storeB);

      const catA = String(a.itemCategory || a.itemCategoryName || "");
      const catB = String(b.itemCategory || b.itemCategoryName || "");
      if (catA !== catB) return catA.localeCompare(catB);

      const itemA = String(a.itemCode || a.itemName || "");
      const itemB = String(b.itemCode || b.itemName || "");
      return itemA.localeCompare(itemB);
    });

    const body = sortedData.map((row, index) => [
      index + 1,
      row.storeName || "-",
      row.itemCategory || "-",
      row.itemCode || "-",
      row.itemDescription || "-",
      row.uom || "-",
      Number(row.quantity || 0).toFixed(3)
    ]);

    autoTable(doc, {
      startY: 50,

      head: headers,
      body,

      theme: "grid",

      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0]
      },

      alternateRowStyles: {
        fillColor: [255, 255, 255]
      },

      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center"
      },

      columnStyles: {
        0: { halign: "right", cellWidth: 15 },
        1: { halign: "left", cellWidth: 45 },
        2: { halign: "left", cellWidth: 45 },
        3: { halign: "left", cellWidth: 50 },
        4: { halign: "left", cellWidth: "auto" },
        5: { halign: "left", cellWidth: 20 },
        6: { halign: "right", cellWidth: 25 }
      },

      margin: {
        top: 50,
        left: 14,
        right: 14,
        bottom: 25
      },

      didDrawPage: function (data) {

        // Skip first page because header already exists
        if (data.pageNumber === 1) return;

        doc.setFont(undefined, "bold");
        doc.setFontSize(12);

        const companyNameWidth = doc.getTextWidth(companyName);
        const logoWidth = 5;
        const logoHeight = 10;
        const logoX = 14 + 6 + (companyNameWidth - logoWidth) / 2;
        const logoY = 8;

        if (pdfLogo) {
          doc.addImage(
            pdfLogo,
            "PNG",
            logoX,
            logoY,
            logoWidth,
            logoHeight
          );
        }

        doc.text(
          companyName,
          14 + 6,
          23
        );

        const title = "Inventory Stock Report (On-Hand)";

        doc.setFontSize(18);

        doc.text(
          title,
          148,
          35,
          { align: "center" }
        );

        const textWidth = doc.getTextWidth(title);

        const centerX =
          148 - (textWidth / 2);

        doc.setLineWidth(0.3);

        doc.line(
          centerX,
          37,
          centerX + textWidth,
          37
        );

        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        doc.text("Project Name:", 14 + 6, 45);
        const labelWidth = doc.getTextWidth("Project Name:");
        doc.setFont(undefined, "normal");
        doc.text(` ${projectLabel}`, 14 + 6 + labelWidth, 45);

        // Report Date
        const reportPrefix = "Report Date: ";
        const reportDateStr = utility.formatExportDateTime(new Date());

        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        const prefixWidth = doc.getTextWidth(reportPrefix);

        doc.setFont(undefined, "normal");
        doc.setFontSize(10);
        const dateWidth = doc.getTextWidth(reportDateStr);

        const totalWidth = prefixWidth + dateWidth;
        const startX = 283 - totalWidth;

        doc.setFont(undefined, "bold");
        doc.text(reportPrefix, startX, 23);

        doc.setFont(undefined, "normal");
        doc.text(reportDateStr, startX + prefixWidth, 23);
      }
    });

    const pageCount = doc.getNumberOfPages();
    const pageHeight = doc.internal.pageSize.getHeight();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      if (i === pageCount) {
        doc.setFont(undefined, "bold");
        doc.setFontSize(8);
        const footerTop = pageHeight - 20;
        doc.text("Prepared By", 25, footerTop);
        doc.text("Checked By", pageWidth / 2, footerTop, { align: "center" });
        doc.text("Approved By", pageWidth - 25, footerTop, { align: "right" });
      }
      doc.setFont(undefined, "normal");
      doc.setFontSize(8);
      const text = `Page ${i} of ${pageCount}`;
      doc.text(text, pageWidth / 2, pageHeight - 7, { align: "center" });
    }

    doc.save(`InventoryStockReport_${utility.formatDateDMY(new Date()).replace(/\//g, "-")}.pdf`);
  };

  // useEffect(() => {
  //   if (currentPage !== 1) {
  //     loadReport();
  //   }
  // }, [currentPage]);

  useEffect(() => {
    setSelectedProjectId("");
    setSelectedStoreId("");
    setSelectedItemCategoryId("");
    setSelectedItemId("");
    setStores([]);
    setItems([]);
  }, [selectedCategoryId]);

  useEffect(() => {
    if (
      selectedProjectId === "" &&
      selectedStoreId === "" &&
      selectedItemCategoryId === "" &&
      selectedItemId === ""
    ) {
      loadReport();
    }
  }, [selectedProjectId, selectedStoreId, selectedItemCategoryId, selectedItemId]);

  const filteredProjects = selectedCategoryId
    ? projects.filter(
      (p) =>
        String(
          p.projectCategoryId ||
          p.categoryId ||
          p.projectCategoryID
        ) === String(selectedCategoryId)
    )
    : [];

  const availableItemCategories = React.useMemo(() => {
    if (!selectedStoreId) return itemCategories;
    const catMap = new Map();
    // Map from master itemCategories list that exist in store items
    itemCategories.forEach((cat) => {
      const catId = String(cat.id || cat.itemCategoryId);
      if (items.some((i) => String(i.itemCategoryId || i.categoryId) === catId)) {
        catMap.set(catId, {
          id: cat.id || cat.itemCategoryId,
          name: cat.itemCategoryName || cat.categoryName || cat.name
        });
      }
    });
    // Also include any itemCategory found directly on store items
    items.forEach((i) => {
      if (i.itemCategoryId && !catMap.has(String(i.itemCategoryId))) {
        catMap.set(String(i.itemCategoryId), {
          id: i.itemCategoryId,
          name: i.itemCategoryName || `Category ${i.itemCategoryId}`
        });
      }
    });
    return Array.from(catMap.values());
  }, [selectedStoreId, items, itemCategories]);

  const sourceItems = items.length > 0 ? items : allItems;

  const filteredItems = selectedItemCategoryId
    ? sourceItems.filter(
      (i) =>
        String(i.itemCategoryId || i.categoryId) ===
        String(selectedItemCategoryId)
    )
    : sourceItems;

  return (
    <>
      <Breadcrumb pageTitle="Inventory Report" secondTitle="Reports" />

      <div className="card p-4 bg-white shadow-sm">

        {/* FILTERS */}
        <div className="row mb-3">


          <div className="col-md-4">
            <label className="form-label">
              Project Category <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              value={selectedCategoryId}
              onChange={(e) => {
                setSelectedCategoryId(e.target.value);
                setSelectedProjectId("");
              }}
            >
              <option value="">Select Category</option>
              {categories.map((c) => (
                <option
                  key={c.id || c.projectCategoryId}
                  value={c.id || c.projectCategoryId}
                >
                  {c.categoryName || c.projectCategoryName || c.name}
                </option>
              ))}
            </select>
          </div>


          {/* PROJECT */}
          <div className="col-md-4">
            <label className="form-label">Project Name <span className="text-danger">*</span></label>
            <select
              className="form-select"
              value={selectedProjectId}
              disabled={!selectedCategoryId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
            >
              <option value="">All Projects</option>
              {filteredProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.projectName}-{p.projectCode}
                </option>
              ))}
            </select>
          </div>

          {/* STORE */}
          <div className="col-md-4">
            <label className="form-label">Store</label>
            <select
              className="form-select"
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              disabled={!selectedProjectId}
            >
              <option value="">Select Store</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.storeName}
                </option>
              ))}
            </select>
          </div>

          {/* ITEM CATEGORY */}
          <div className="col-md-4 mt-3">
            <label className="form-label">Item Category</label>
            <select
              className="form-select"
              value={selectedItemCategoryId}
              onChange={(e) => {
                setSelectedItemCategoryId(e.target.value);
                setSelectedItemId("");
              }}
              disabled={!selectedProjectId}
            >
              <option value="">All Item Categories</option>
              {availableItemCategories.map((ic) => (
                <option
                  key={ic.id}
                  value={ic.id}
                >
                  {ic.name || ic.itemCategoryName || ic.categoryName}
                </option>
              ))}
            </select>
          </div>

          {/* ITEM */}
          <div className="col-md-4 mt-3">
            <label className="form-label">Item</label>
            <select
              className="form-select"
              value={selectedItemId}
              onChange={(e) => setSelectedItemId(e.target.value)}
              disabled={!selectedProjectId}
            >
              <option value="">All Items</option>
              {filteredItems.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.itemCode}
                </option>
              ))}
            </select>
          </div>

        </div>

        {/* VIEW BUTTON */}

        <div className="mb-3 d-flex justify-content-end align-items-center">


          <div className="d-flex gap-2">
            <button
              className="btn btn-secondary"
              onClick={handleClearFilters}
            >
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>

            <button
              className="btn btn-primary"
              onClick={handleSearchClick}
              disabled={!selectedCategoryId || !selectedProjectId}
            >
              <i className="ti ti-search me-1"></i>
              View Report
            </button>
          </div>


        </div>

        <hr></hr>
        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button
              className="btn btn-success"
              onClick={exportPdf}
              disabled={tableLoading || tableData.length === 0}
            >
              <i className="ti ti-file-type-pdf me-1"></i>
              Export PDF
            </button>

            <button
              className="btn btn-success"
              onClick={exportExcel}
              disabled={tableLoading || tableData.length === 0}
            >
              <i className="ti ti-download me-1"></i>
              Export Excel
            </button>
          </div>




        </div>
        {/* TABLE */}
        <div className="table-responsive">

          <table className="table table-bordered table-striped">
            <thead className="table-light">
              <tr>
                <th className="text-end">Sr.No</th>
                <th>Project</th>
                <th>Store Name</th>
                <th>Item Category</th>
                <th>Item Name</th>
                <th>Description</th>
                <th>UOM</th>
                <th className="text-end">Sum of stockIn</th>
                <th className="text-end">Sum of stockOut</th>
                <th className="text-end">Quantity</th>
              </tr>
            </thead>

            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan="10" className="text-center">
                    <div className="spinner-border text-primary" />
                  </td>
                </tr>
              ) : tableData.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center">
                    No data found
                  </td>
                </tr>
              ) : (
                tableData.map((row, index) => (
                  <tr key={index}>
                    {/* <td>{index + 1}</td> */}
                    <td className="text-end">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{row.projectName ? (() => {
                      const cat = row.projectCategory || row.projectCategoryName || row.categoryName || row.category || (projects.find(p => String(p.id || p.projectId) === String(row.projectId || selectedProjectId) || (row.projectName && String(p.projectName).trim().toLowerCase() === String(row.projectName).trim().toLowerCase()))?.projectCategory) || (projects.find(p => String(p.id || p.projectId) === String(row.projectId || selectedProjectId) || (row.projectName && String(p.projectName).trim().toLowerCase() === String(row.projectName).trim().toLowerCase()))?.categoryName) || "";
                      const init = String(cat).trim().charAt(0).toUpperCase();
                      return init ? `${init}*${row.projectName}` : row.projectName;
                    })() : "-"}</td>
                    <td>{row.storeName}</td>
                    <td>{row.itemCategory || "-"}</td>
                    <td>{row.itemCode}</td>
                    <td>{row.itemDescription}</td>
                    <td>{row.uom}</td>
                    <td className="text-end">{row.sumOfStockIn}</td>
                    <td className="text-end">{row.sumOfStockOut}</td>
                    <td className="text-end">{row.quantity}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={handlePageClick}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            pageCount={pageCount}
            previousLabel="< Previous"
            containerClassName="pagination-container"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            activeClassName="active"
          />

        </div>
      </div>
    </>
  );
}
